import { getXsrfToken, methodRequiresXsrfToken } from "./fetchInterceptorUtils";

export const antiForgeryMiddleware = async (req: Request, baseUrl?: string) => {
  if (!methodRequiresXsrfToken(req.method)) return req;
  const token = await getXsrfToken(baseUrl);
  if (token) {
    req.headers.set("X-XSRF-TOKEN", token);
  }
  return req;
};

const injectRecaptchaToken = (
  bodyText: string | undefined,
  token: string,
): string => {
  if (!bodyText) {
    return JSON.stringify({ token });
  }
  try {
    const parsed = JSON.parse(bodyText);
    if (parsed && typeof parsed === "object") {
      (parsed as Record<string, unknown>).token = token;
      return JSON.stringify(parsed);
    }
  } catch {
    // Non-JSON body; leave unchanged.
  }
  return bodyText; // unchanged if not JSON object
};

function methodRequiresRecaptcha(method?: string) {
  return method === "POST";
}

let recaptchaScriptLoadPromise: Promise<void> | undefined;

function findRecaptchaScript(recaptchaSiteKey: string) {
  return Array.from(document.scripts).find((script) => {
    try {
      const url = new URL(script.src);
      return (
        url.pathname.endsWith("/recaptcha/enterprise.js") &&
        url.searchParams.get("render") === recaptchaSiteKey
      );
    } catch {
      return false;
    }
  });
}

async function loadRecaptchaScript(recaptchaSiteKey: string): Promise<void> {
  if (globalThis.grecaptcha) {
    return;
  }

  if (!recaptchaScriptLoadPromise) {
    const existingScript = findRecaptchaScript(recaptchaSiteKey);
    if (existingScript) {
      recaptchaScriptLoadPromise = new Promise<void>((resolve, reject) => {
        const handleLoad = () => {
          resolve();
        };
        const handleError = () => {
          recaptchaScriptLoadPromise = undefined;
          reject(new Error("Failed to load recaptcha script"));
        };

        existingScript.addEventListener("load", handleLoad, { once: true });
        existingScript.addEventListener("error", handleError, { once: true });
      });
    } else {
      recaptchaScriptLoadPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.async = true;
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(recaptchaSiteKey)}`;
        script.onload = () => {
          resolve();
        };
        script.onerror = () => {
          recaptchaScriptLoadPromise = undefined;
          reject(new Error("Failed to load recaptcha script"));
        };

        const mountPoint =
          document.head ?? document.body ?? document.documentElement;
        mountPoint.appendChild(script);
      });
    }
  }

  await recaptchaScriptLoadPromise;
}

async function grecaptchaWrapper(action: string): Promise<string | undefined> {
  const recaptchaSiteKey = globalThis.recaptchaSiteKey;
  if (!recaptchaSiteKey) {
    return undefined;
  }

  if (!globalThis.grecaptcha) {
    await loadRecaptchaScript(recaptchaSiteKey);
  }

  if (globalThis.grecaptcha) {
    return await new Promise<string | undefined>((resolve, reject) => {
      globalThis.grecaptcha.enterprise.ready(async () => {
        try {
          const token = await globalThis.grecaptcha.enterprise.execute(
            recaptchaSiteKey,
            {
              action,
            },
          );
          return resolve(token);
        } catch {
          reject(new Error("Failed to execute grecaptcha"));
        }
      });
    });
  }
  return undefined;
}

export const recaptchaMiddleware = async (req: Request) => {
  // Early exits reduce nesting and complexity.
  if (!methodRequiresRecaptcha(req.method)) return req;
  const recaptchaToken = await grecaptchaWrapper("action");
  if (!recaptchaToken) return req;

  let existingBodyText: string | undefined;
  try {
    const buf = await req.clone().arrayBuffer();
    if (buf && buf.byteLength > 0) {
      existingBodyText = new TextDecoder("utf-8").decode(buf);
      if (existingBodyText === "") existingBodyText = undefined;
    } else {
      existingBodyText = undefined;
    }
  } catch {
    // Fallback to text() if arrayBuffer fails for an unexpected reason.
    try {
      existingBodyText = await req.clone().text();
      if (existingBodyText === "") existingBodyText = undefined;
    } catch {
      existingBodyText = undefined;
    }
  }

  const updatedBodyText = injectRecaptchaToken(
    existingBodyText,
    recaptchaToken,
  );

  // Only create new Request if body actually changed (covers empty body case too)
  if (updatedBodyText !== existingBodyText) {
    // Preserve headers explicitly; passing headers ensures we don't lose
    // content-type or other important headers when constructing the new Request.
    const preservedHeaders = new Headers(req.headers);
    if (!preservedHeaders.has("Content-Type")) {
      preservedHeaders.set("Content-Type", "application/json");
    }
    req = new Request(req, {
      body: updatedBodyText,
      headers: preservedHeaders,
    });
  }
  return req;
};
